/**
 * A mixin to fetch filtered RadioSpot/TVSpotBundles/Tickers (paginated).
 *
 * Spots/Tickers are queried in pages and appended to the existing
 * results. This is because of the amount of entries and so that it
 * works together with an infinite scroll. See the
 * AddContentToDecoder.vue component for an example of that.
 *
 * Depends on:
 *   - this.$route
 *
 * Components that use this mixin will have to make use of the computed
 * values in here like:
 *
 *   - this.ctypes
 *   - this.contentsQuery
 *   - this.lastContentsQuery
 *
 * And of the explicit computed props that can be used as a v-model on
 * widgets directly, but are still stored in the query object:
 *
 *   - this.contentsQuerySearchText
 *   - this.contentsQuerySelectedCategoryIds
 *
 * Caching
 * ~~~~~~~
 *
 * See our github wiki for additional details on caching and filtering:
 * https://github.com/myshopradio/cms/wiki/Implementation-Details
 *
 * The list of Contents is cached and remembered when we get back to
 * the page the cache has been created on. Together with the
 * rememberPageScrollPos.js mixin this makes for improved UX. Cache
 * will be invalidated when:
 *
 *   a) the contents query has been changed in any way
 *   b) we navigate to a different route that needs the contentlist
 *      but is not the one the cache has been created for
 *
 * So basically a page using this contentlist will cache it and we can
 * navigate elsewhere and come back without having to reload every time,
 * but if we visit another consuming page than the cache is gone. The
 * primary use case for this is to browse the list and jump to specific
 * Spots from it to preview them and where they are being used before
 * going back to the list.
 *
 */

import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';

import axios from 'axios';

import environment from '@/environment';
import {deepCopyObject, ctypesObjToArr} from '@/utils';

var fetchContents = {
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    lastQueryCount: {
      get() {
        return this.$store.state.common.lastContentsQueryCount;
      },
      set(count) {
        this.$store.commit('SET_LAST_CONTENTS_QUERY_COUNT', count);
      },
    },
    contentsQuery() {
      return this.$store.state.common.contentsQuery;
    },
    lastContentsQuery() {
      return this.$store.state.common.lastContentsQuery;
    },
    contentsQuerySearchText: {
      get() {
        return this.$store.state.common.contentsQuery.searchText;
      },
      set(searchText) {
        this.$store.commit('SET_CONTENTS_QUERY_SEARCH_TEXT', searchText);
      },
    },
    contentsQuerySelectedCategoryIds: {
      get() {
        return this.$store.state.common.contentsQuery.selectedCategoryIds;
      },
      set(selectedCategoryIds) {
        this.$store.commit(
          'SET_CONTENTS_QUERY_SELECTED_CATEGORY_IDS',
          selectedCategoryIds
        );
      },
    },
    contentsQuerySelectedTagIds: {
      get() {
        return this.$store.state.common.contentsQuery.selectedTagIds;
      },
      set(selectedTagIds) {
        this.$store.commit(
          'SET_CONTENTS_QUERY_SELECTED_TAG_IDS',
          selectedTagIds
        );
      },
    },
    contentsCachedForRoutePath: {
      get() {
        return this.$store.state.common.contentsCachedForRoutePath;
      },
      set(path) {
        this.$store.commit('SET_CONTENTS_CACHED_FOR_ROUTE_PATH', path);
      },
    },
    /**
     * Currently selected ctypes as list of strings like: ['tv', 'tickers].
     *
     * If no ctypes are selected, interpret as 'all selected'.
     */
    ctypes() {
      const ctypesObj = {
        radio: this.contentsQuery.radio,
        tv: this.contentsQuery.tv,
        tickers: this.contentsQuery.tickers,
      };
      const ctypesArr = ctypesObjToArr(ctypesObj);
      return ctypesArr;
    },
    /**
     * Previously selected ctypes as list of strings like: ['tv', 'tickers].
     */
    lastCtypes() {
      const ctypesObj = {
        radio: this.lastContentsQuery.radio,
        tv: this.lastContentsQuery.tv,
        tickers: this.lastContentsQuery.tickers,
      };
      const ctypesArr = ctypesObjToArr(ctypesObj);
      return ctypesArr;
    },
    /**
     * The last page index we fetched for the current query.
     */
    contentsPage: {
      get() {
        return this.$store.state.common.contentsPage;
      },
      set(page) {
        this.$store.commit('SET_CONTENTS_PAGE', page);
      },
    },
    /**
     * How many pages the current query gives us.
     */
    contentsNumPages: {
      get() {
        return this.$store.state.common.contentsNumPages;
      },
      set(numPages) {
        this.$store.commit('SET_CONTENTS_NUM_PAGES', numPages);
      },
    },
    /**
     * The currently fetched list of contents (RadioSpot, TVSpotBundle, Ticker).
     */
    contents: {
      get() {
        return this.$store.state.common.contents;
      },
      set(contents) {
        this.$store.commit('SET_CONTENTS', contents);
      },
    },
    contentsQueryIsUnchanged() {
      const sameCtypes = isEqual(this.ctypes, this.lastCtypes);

      const sameSearch =
        this.contentsQuery.searchText === this.lastContentsQuery.searchText;

      const sameCategories = isEqual(
        this.contentsQuery.selectedCategoryIds,
        this.lastContentsQuery.selectedCategoryIds
      );

      const sameTags = isEqual(
        this.contentsQuery.selectedTagIds,
        this.lastContentsQuery.selectedTagIds
      );

      const sameOnAirOnly =
        this.contentsQuery.onAirOnly === this.lastContentsQuery.onAirOnly;

      const sameEditableOnly =
        this.contentsQuery.editableOnly === this.lastContentsQuery.editableOnly;

      const sameUnpublishedOnly =
        this.contentsQuery.unpublishedOnly ===
        this.lastContentsQuery.unpublishedOnly;

      const samePublishedOnly =
        this.contentsQuery.publishedOnly ===
        this.lastContentsQuery.publishedOnly;

      const sameMinDate =
        this.contentsQuery.minDate === this.lastContentsQuery.minDate;

      const sameMaxDate =
        this.contentsQuery.maxDate === this.lastContentsQuery.maxDate;

      const sameGroups = isEqual(
        this.contentsQuery.selectedGroupIds,
        this.lastContentsQuery.selectedGroupIds
      );

      const unchanged =
        sameCategories &&
        sameCtypes &&
        sameGroups &&
        sameOnAirOnly &&
        sameEditableOnly &&
        samePublishedOnly &&
        sameMinDate &&
        sameMaxDate &&
        sameSearch &&
        sameTags &&
        sameUnpublishedOnly;

      return unchanged;
    },
    contentsQueryHasChanged() {
      return !this.contentsQueryIsUnchanged;
    },
  },
  watch: {
    /**
     * React to content query changes and store last content query.
     *
     * We watch attributes separately, because we want to have fine
     * control over things like debounce() times, e.g. .searchText
     * changes will trigger @input signals, which calls autoSearch()
     * which has a different debounce setup than the other filters.
     */
    'contentsQuery.radio': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.tv': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.tickers': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.onAirOnly': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.editableOnly': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.unpublishedOnly': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.publishedOnly': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.minDate': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.maxDate': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.selectedCategoryIds': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.selectedTagIds': function () {
      this.onWatchedQueryAttributeChanged();
    },
    'contentsQuery.selectedGroupIds': function () {
      this.onWatchedQueryAttributeChanged();
    },
  },
  mounted() {
    // Allow invalidation from anywhere in the app via global signal.
    this.$bus.$on('invalidate-contents-cache', this.invalidateContentsCache_);
  },
  methods: {
    onWatchedQueryAttributeChanged: debounce(function () {
      console.log('Invalidating contents cache due to query change');
      const cancelPriorRequest = true;
      this.refetchContents(cancelPriorRequest);
    }, 300),
    /**
     * Reset content list and page and fetch anew while keeping query.
     *
     * Arguments are passed from the current route and ctypes list.
     */
    refetchContents(cancelPriorRequest = false) {
      const forceRefetch = true;
      return this.fetchNextContents_(
        this.ctypes,
        forceRefetch,
        this.$route.params.decoderId,
        this.$route.params.ctype,
        cancelPriorRequest
      );
    },
    /**
     * Fetch next page of content list for current query.
     *
     * Arguments are passed from the current route and ctypes list.
     */
    fetchNextContents() {
      const forceRefetch = false;
      this.fetchNextContents_(
        this.ctypes,
        forceRefetch,
        this.$route.params.decoderId,
        this.$route.params.ctype
      );
    },
    /**
     * Trigger a search request while aborting the previous request.
     *
     * Args:
     *   ctypes (String): List of content types to return. Possible
     *     values: 'radio', 'tv', 'ticker'. Simply used here to
     *     pass to fetchSpots().
     *
     *   targetDecoderId (Number): If specified, resulting Spots list
     *     will consider compatibility to this Decoder and omit Spots
     *     that already run on it.
     *
     *   targetDecoderCtype (String): Complementary to targetDecoderId,
     *     telling the server which Decoder class to use. Possible
     *     values: 'radio', 'tv'.
     *
     * Do not trigger if the search text is too short, unless we
     * cleared the search field completely compared to the last search.
     * The call itself is debounced to avoid spamming requests during
     * typing.
     */
    autoSearch: debounce(function (
      targetDecoderId = null,
      targetDecoderCtype = null
    ) {
      const minChars = 2;
      if (
        this.contentsQuery.searchText &&
        this.contentsQuery.searchText.length < minChars
      ) {
        const lastWasNotEmpty = this.lastContentsQuery.searchText !== '';
        const thisOneIsEmpty = this.contentsQuery.searchText === '';
        if (!(lastWasNotEmpty && thisOneIsEmpty)) {
          return;
        }
      }
      const forceRefetch = false;
      const cancelPriorRequests = true;
      this.fetchNextContents_(
        this.ctypes,
        forceRefetch,
        targetDecoderId,
        targetDecoderCtype,
        cancelPriorRequests
      );
    },
    250),
    /**
     * Fetch next page of Contents and append them to the list.
     *
     * This is the core internal function of this module, with the
     * public functions being wrappers around it.
     *
     * We automatically detect if the query has changed from the last
     * time and handle both cases:
     *   - Query unchanged: Fetch next page
     *   - Query changed: Reset and refetch anew
     *
     * Args:
     *   ctypes (String): List of content types to return. Possible
     *     values: 'radio', 'tv', 'ticker'. If this value is
     *     undefined, reset the contents list.
     *
     *   forceRefetch (Boolean): Optional. If true, the current content
     *     list and page are reset so that contents are refetched anew,
     *     no matter if the query changed or not.
     *
     *   targetDecoderId (Number): Optional. If specified, resulting
     *     Contents list will consider compatibility to this Decoder
     *     and omit contents that already run on it.
     *
     *   targetDecoderCtype (String): Optional and complementary to
     *     targetDecoderId, telling the server which Decoder class to
     *     use. Possible values: 'radio', 'tv'.
     *
     *   cancelPriorRequests (Boolean): Optional. When given all
     *     current requests (aka Promises) wilĺ be cancelled. Makes
     *     sense to use in conjunction e.g. with automatic search.
     *
     * Note: We omit the Spot's .entries field here to speed up
     *   serialization.
     */
    fetchNextContents_(
      ctypes,
      forceRefetch = false,
      targetDecoderId = null,
      targetDecoderCtype = null,
      cancelPriorRequest = false
    ) {
      // If we changed the query, the pages may be different. In that
      // case, reset the contents list and start at page 1 again.
      let queryParams = {
        categories: this.contentsQuery.selectedCategoryIds.join(','),
        ctypes: ctypes.join(','),
        groups: this.contentsQuery.selectedGroupIds.join(','),
        omit: 'entries',
        search: this.contentsQuery.searchText,
        tags: this.contentsQuery.selectedTagIds.join(','),
      };
      if (this.contentsQuery.onAirOnly) {
        queryParams.on_air_only = true;
      }
      if (this.contentsQuery.editableOnly) {
        queryParams.editable_only = true;
      }
      if (this.contentsQuery.unpublishedOnly) {
        queryParams.unpublished_only = true;
      }
      if (this.contentsQuery.publishedOnly) {
        queryParams.published_only = true;
      }
      if (this.contentsQuery.minDate) {
        queryParams.min_date = this.contentsQuery.minDate;
      }
      if (this.contentsQuery.maxDate) {
        queryParams.max_date = this.contentsQuery.maxDate;
      }

      if (targetDecoderId && targetDecoderCtype) {
        const pair = targetDecoderCtype + ',' + targetDecoderId;
        queryParams.target_decoder = pair;
      }

      if (this.contentsQueryHasChanged || forceRefetch) {
        // Reset the contents list and fetch anew.
        this.resetContents_();
        this.contentsPage = 1;
      } else {
        // Just fetch more data.
        const nextPage = this.contentsPage + 1;
        if (nextPage > this.contentsNumPages) {
          // There is no more data to fetch, bail out.
          return new Promise((resolve, reject) => {
            resolve();
          });
        }
        this.contentsPage = nextPage;
      }

      queryParams.page = this.contentsPage;

      if (cancelPriorRequest) {
        // Note: Right now only content requests are marked as
        //   cancellable, so only these will get killed here. If we
        //   need more cancellable request types in the future than
        //   we need to use multiple cancellation tokens.
        this.$api.cancelCurrentRequest();
      }

      return this.fetchContents_(queryParams);
    },
    rememberCurrentQueryAsLastQuery() {
      this.$store.commit(
        'SET_LAST_CONTENTS_QUERY',
        deepCopyObject(this.contentsQuery)
      );
    },
    resetContents_() {
      this.$store.commit('RESET_CONTENTS');
    },
    resetPage_() {
      // Actual pages start at 1, but reset to 0 cause we always append 1.
      this.contentsPage = 0;
      this.contentsNumPages = 1;
    },
    /*
     * It should not, but may happen accidentally that contents are
     * fetched multiple times and we would end up with duplicates
     * in the final contentlist (and from that, vue v-for duplicate
     * key errors). I repeat, it should not happen, since we cancel
     * duplicate requests, but we may make mistakes: In that case,
     * we prefer to have this fixed automatically, so that only one
     * version of the content is listed.
     */
    mergeAndIgnoreDuplicates(newContents) {
      const existingContents = this.contents;
      const existingContentIds = existingContents.map((content) => content.id);
      let mergedContents = [].concat(this.contents);
      newContents.forEach((content) => {
        const isDuplicate = existingContentIds.indexOf(content.id) !== -1;
        if (!isDuplicate) {
          mergedContents.push(content);
        }
        if (isDuplicate) {
          console.warn('Duplicate content', content);
        }
      });
      return mergedContents;
    },
    /**
     * Fetch a page of contents and concat it to the current list.
     */
    fetchContents_(queryParams) {
      this.loading = true;
      this.rememberCurrentQueryAsLastQuery();

      return this.$api
        .listUserContents(queryParams)
        .then((response) => {
          const newContents = response.data.results;
          const mergedContents = this.mergeAndIgnoreDuplicates(newContents);
          this.contents = mergedContents;

          this.lastQueryCount = response.data.count;
          this.contentsNumPages = response.data.num_pages;
          this.contentsCachedForRoutePath = this.$route.path;

          this.loading = false;
        })
        .catch((error) => {
          // // Cancellation is fine, ignore it.
          if (axios.isCancel(error)) {
            return new Promise((resolve, reject) => {
              resolve();
            });
          }
        });
    },
    refetchContentsBasedOnStoreFlag(toggle = true) {
      if (!this.$store.state.common.contentsForceRefetchOnNextVisit) {
        return;
      }
      console.log('[DEBUG] Refetch triggered via store state');
      if (toggle) {
        this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', false);
      }
      const cancelPriorRequest = true;
      this.refetchContents(cancelPriorRequest);
    },
    invalidateCacheOnIncompatibleRouteDetected(vm, nextRoute) {
      const somethingHasBeenCached = vm.contentsCachedForRoutePath !== null;
      if (!somethingHasBeenCached) {
        return;
      }
      const nextRouteIncompatibleToCache =
        nextRoute.path !== vm.contentsCachedForRoutePath;
      if (nextRouteIncompatibleToCache) {
        console.log('Invalidating contents cache due to route change');
        vm.invalidateContentsCache_();

        const cancelPriorRequest = true;
        vm.refetchContents(cancelPriorRequest);
      }
    },
    invalidateContentsCache_() {
      this.$store.commit('INVALIDATE_CONTENTS_CACHE');
    },
  },
  /**
   * Invalidate the cache if the next route does not match it.
   *
   * Refetch content with current filters if flagged, e.g. from places
   * where content can be modified and which needs to be refreshed to
   * ensure they still match or don't match the filters.
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (environment.contentsCachingEnabled) {
        vm.invalidateCacheOnIncompatibleRouteDetected(vm, to);
        vm.refetchContentsBasedOnStoreFlag();
      } else {
        vm.invalidateContentsCache_();
      }
    });
  },
};

export default fetchContents;



// WEBPACK FOOTER //
// ./src/mixins/fetchContents.js